import { useCallback, useContext, useRef, useState } from 'react'
import { useIntl } from 'react-intl'

import { useAppDispatch, shallowEqual, useAppSelector } from 'hooks/reduxHooks'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import uuidV4 from 'lib/string/uuidV4Utils'
import CurrencyContext from 'contexts/currencyContext'
import { getCartMachineActor } from 'machines/cart/MultiCartMachineProvider'
import { transformUserCartItemsToCheckoutItems } from 'lib/checkout/userCart'
import { initialiseCheckout } from 'actions/CheckoutActions'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import { useShouldWaiveLuxPlusJoiningFee } from 'hooks/UserCart/useCartItems'
import { isExperienceItem } from 'lib/checkout/checkoutUtils'
import { isMultiCartFlightItem } from 'components/Pages/UserCartPage/SharedCarts/multiCartUtils'
import type { CartPanelItem } from './useCartPanelItems'
import {
  getCartPanelExperienceTimesKey,
  isCartPanelItemCheckoutReady,
  prepareCartPanelExperienceCheckoutItem,
} from '../utils/cartPanelUtils'
import { refreshTripChatBedbankItemSessions } from '../utils/tripChatCheckoutBedbankUtils'

interface UseTripChatCheckoutActionsArgs {
  chatId: number | undefined
  multiCartId: string | undefined
  refetchCartItems: () => Promise<Map<string, App.MultiCartItem>>
}

interface UseTripChatCheckoutActionsReturn {
  checkoutCartItems: (items: Array<CartPanelItem>) => Promise<void>
  isCheckingOut: boolean
}

function isCheckoutReadyItem(item: CartPanelItem): item is CartPanelItem & { cartItem: App.MultiCartItem } {
  return isCartPanelItemCheckoutReady(item) && !!item.cartItem
}

function withFreshCartItemSnapshot(
  item: CartPanelItem,
  cartItems: Map<string, App.MultiCartItem>,
): CartPanelItem {
  if (!item.cartItemId) return item
  const cartItem = cartItems.get(item.cartItemId)
  if (item.productType === 'flight') {
    return {
      ...item,
      cartItem,
      flightCartItem: cartItem && isMultiCartFlightItem(cartItem) ? cartItem : undefined,
    }
  }

  return {
    ...item,
    cartItem,
  }
}

function toCheckoutItem(
  item: CartPanelItem & { cartItem: App.MultiCartItem },
  currency: string,
  experiences: App.State['experience']['experiences'],
  experienceTimes: App.State['experience']['experienceTimes'],
): App.Checkout.AnyItem {
  const checkoutItem = {
    ...item.cartItem.item,
    itemId: item.cartItem.item.itemId ?? item.cartItem.id,
    transactionKey: item.cartItem.item.transactionKey ?? uuidV4(),
  }
  if (item.productType !== 'experience' || !isExperienceItem(checkoutItem)) return checkoutItem

  const key = getCartPanelExperienceTimesKey(checkoutItem, currency)
  return prepareCartPanelExperienceCheckoutItem(
    checkoutItem,
    experiences[checkoutItem.experienceId],
    experienceTimes[checkoutItem.experienceId]?.[key]?.slots,
  )
}

export function useTripChatCheckoutActions({
  chatId,
  multiCartId,
  refetchCartItems,
}: UseTripChatCheckoutActionsArgs): UseTripChatCheckoutActionsReturn {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const currency = useContext(CurrencyContext)
  const shouldWaive = useShouldWaiveLuxPlusJoiningFee()
  const checkoutInFlightRef = useRef(false)
  const [isCheckingOut, setIsCheckingOut] = useState(false)
  const { subscriptionOffers, experiences, experienceTimes } = useAppSelector(state => ({
    subscriptionOffers: state.luxPlus.products.subscription.offers,
    experiences: state.experience.experiences,
    experienceTimes: state.experience.experienceTimes,
  }), shallowEqual)

  const defaultErrorMessage = intl.formatMessage({
    id: 'EmZPR-KxU7oRFczGeJk2',
    defaultMessage: 'Something went wrong. Please try again.',
    description: 'Default error message when a chat streaming request fails',
  })

  const checkoutCartItems = useCallback(async(items: Array<CartPanelItem>) => {
    if (!chatId || !multiCartId || checkoutInFlightRef.current) return
    checkoutInFlightRef.current = true
    setIsCheckingOut(true)

    try {
      let freshCartItems: Map<string, App.MultiCartItem>
      try {
        freshCartItems = await refetchCartItems()
      } catch {
        showSnackbar(defaultErrorMessage, 'critical')
        return
      }

      const activeItems = items
        .map(item => withFreshCartItemSnapshot(item, freshCartItems))
        .filter(isCheckoutReadyItem)
      if (activeItems.length === 0) return

      getCartMachineActor()?.send({ type: 'set active cart', cartId: multiCartId })

      const checkoutSource: App.CheckoutSource = {
        type: 'trip_planner',
        multiCartId,
        externalReferenceId: String(chatId),
      }
      const checkoutItems = activeItems.map(item => toCheckoutItem(item, currency, experiences, experienceTimes))
      let transformed = transformUserCartItemsToCheckoutItems(checkoutItems, subscriptionOffers, shouldWaive)
      try {
        transformed = await refreshTripChatBedbankItemSessions(transformed, dispatch)
      } catch {
        showSnackbar(defaultErrorMessage, 'critical')
        return
      }

      dispatch(initialiseCheckout(transformed.map(item => ({ ...item, transactionKey: uuidV4() })), { checkoutSource }, true))
      dispatch(navigateToCheckout({ skipSpoofedModal: true }))
    } finally {
      checkoutInFlightRef.current = false
      setIsCheckingOut(false)
    }
  }, [chatId, multiCartId, refetchCartItems, defaultErrorMessage, currency, experiences, experienceTimes, subscriptionOffers, shouldWaive, dispatch])

  return { checkoutCartItems, isCheckingOut }
}
